import { ChangeEvent, FC } from "react"
import { useAppDispatch, useAppSelector } from "@/store"
import {
  DevToolsSelectors,
  ITgInsets,
  ITgInsetsPresetId,
  resetTgInsets,
  setTgInsetValue,
  setTgInsetsPreset
} from "@/store/slices/devTools"
import { Button } from "@/ui/Button"
import styles from "./TgInsetsSection.module.scss"

const PRESET_OPTIONS: {
  id: Exclude<ITgInsetsPresetId, "custom">
  label: string
}[] = [
  { id: "off", label: "Off" },
  { id: "iphone-15-pro-landscape", label: "iPhone 15 Pro landscape" }
]

const SAFE_AREA_KEYS: (keyof ITgInsets)[] = [
  "safe-area-inset-top",
  "safe-area-inset-bottom",
  "safe-area-inset-left",
  "safe-area-inset-right"
]

const CONTENT_SAFE_AREA_KEYS: (keyof ITgInsets)[] = [
  "content-safe-area-inset-top",
  "content-safe-area-inset-bottom",
  "content-safe-area-inset-left",
  "content-safe-area-inset-right"
]

const sideLabel = (key: keyof ITgInsets): string => {
  if (key.endsWith("-top")) return "top"
  if (key.endsWith("-bottom")) return "bottom"
  if (key.endsWith("-left")) return "left"
  return "right"
}

export const TgInsetsSection: FC = () => {
  const dispatch = useAppDispatch()
  const insets = useAppSelector(DevToolsSelectors.selectTgInsets())
  const presetId = useAppSelector(DevToolsSelectors.selectTgInsetsPresetId())

  const handlePresetChange = (e: ChangeEvent<HTMLSelectElement>) => {
    dispatch(setTgInsetsPreset(e.target.value as ITgInsetsPresetId))
  }

  const handleInsetChange =
    (key: keyof ITgInsets) => (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const value = raw === "" ? 0 : Number(raw)
      dispatch(setTgInsetValue({ key, value }))
    }

  const handleReset = () => {
    dispatch(resetTgInsets())
  }

  return (
    <section className={styles.TgInsetsSection}>
      <h3 className={styles.TgInsetsSection_title}>Telegram Insets</h3>

      <label className={styles.TgInsetsSection_presetRow}>
        <span className={styles.TgInsetsSection_label}>Preset</span>
        <select
          className={styles.TgInsetsSection_presetSelect}
          value={presetId}
          onChange={handlePresetChange}
        >
          {PRESET_OPTIONS.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
          <option value="custom" disabled>
            Custom
          </option>
        </select>
      </label>

      <fieldset className={styles.TgInsetsSection_group}>
        <legend className={styles.TgInsetsSection_legend}>Safe area</legend>
        <div className={styles.TgInsetsSection_grid}>
          {SAFE_AREA_KEYS.map((key) => (
            <label key={key} className={styles.TgInsetsSection_field}>
              <span className={styles.TgInsetsSection_label}>
                {sideLabel(key)}
              </span>
              <input
                type="number"
                min={0}
                step={1}
                className={styles.TgInsetsSection_input}
                value={insets[key]}
                onChange={handleInsetChange(key)}
              />
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className={styles.TgInsetsSection_group}>
        <legend className={styles.TgInsetsSection_legend}>
          Content safe area
        </legend>
        <div className={styles.TgInsetsSection_grid}>
          {CONTENT_SAFE_AREA_KEYS.map((key) => (
            <label key={key} className={styles.TgInsetsSection_field}>
              <span className={styles.TgInsetsSection_label}>
                {sideLabel(key)}
              </span>
              <input
                type="number"
                min={0}
                step={1}
                className={styles.TgInsetsSection_input}
                value={insets[key]}
                onChange={handleInsetChange(key)}
              />
            </label>
          ))}
        </div>
      </fieldset>

      <Button
        className={styles.TgInsetsSection_resetButton}
        styles="yellow"
        onClick={handleReset}
      >
        Reset
      </Button>
    </section>
  )
}
